import React from 'react'
import { Link } from 'react-router'

import { Container, Row, Col } from 'components/Layout'
import Page from 'components/Page'
import PageHeader from 'components/Page/PageHeader'
import HeadingBanner from 'components/Text/HeadingBanner'
import { Crumbs, Crumb } from 'components/BreadCrumbs'
import Button from 'components/Button'
import InfoCard from 'components/Cards/InfoCard'
import StickyChannel from 'components/StickyChannel'
import SimpleTable from 'components/Tables/SimpleTable'
import IconPhone from 'components/Icons/Phone'
import LoadingOverlay from 'components/Loading/LoadingOverlay'

import {
  useDeletePhoneSystemMutation,
  useGetPhoneSystemServiceQuery,
  useDeletePhoneSystemExtensionMutation,
  useDeletePhoneSystemHuntGroupMutation,
  useDeletePhoneSystemNumberMutation,
} from 'src/generated/graphql'

import { dispatchToProps } from './PhoneSystemNewEdit.container'

import cn from 'classnames'
import sText from 'components/Text/Text.scss'
import sLay from 'styles/modules/layout.scss'
import sMarg from 'styles/modules/margins.scss'
import s from './PhoneSystemNewEdit.scss'

type IDispatchToProps = typeof dispatchToProps

export interface IPhoneSystemNewEditComponentProps extends IDispatchToProps {
  routeParams: {
    phoneSystemId: string
  }
  isAdmin: boolean
}

export const PhoneSystemNewEditComponent = (props: IPhoneSystemNewEditComponentProps) => {
  const {
    routeParams: { phoneSystemId },
    isAdmin,
  } = props

  const { data, loading } = useGetPhoneSystemServiceQuery({ variables: { id: phoneSystemId } })

  const [deletePhoneSystemMutation, deletePhoneSystemStatus] = useDeletePhoneSystemMutation({
    variables: { phoneSystemId },
  })

  const [deleteExtensionMutation, deleteExtensionStatus] = useDeletePhoneSystemExtensionMutation()
  const [deleteNumberMutation, deleteNumberStatus] = useDeletePhoneSystemNumberMutation()
  const [deleteHuntGroupMutation, deleteHuntGroupStatus] = useDeletePhoneSystemHuntGroupMutation()

  if (loading || !data || !data.phoneSystem || deletePhoneSystemStatus.loading) {
    return (
      <Page layout={{ header: 'app' }} title="Phone Systems">
        <LoadingOverlay transparent />
      </Page>
    )
  }

  const service = data?.phoneSystem

  const flow = service?.numbers[0] && service?.numbers[0].flow

  return (
    <Page layout={{ header: 'app' }} title="Phone Systems">
      <PageHeader>
        <Crumbs title="Hosted Voice">
          <Crumb parent to="/dashboard/hosted-voice">
            Services
          </Crumb>
          <Crumb parent to="/dashboard/hosted-voice/phone-systems">
            Phone Systems
          </Crumb>
          <Crumb active to={`/dashboard/hosted-voice/phone-systems/${service?.id}`}>
            {service?.name}
          </Crumb>
        </Crumbs>
        <HeadingBanner>
          <h3 className={cn(sText.heading3, sText.accent)}>Phone System: {service?.name}</h3>
        </HeadingBanner>
      </PageHeader>
      <div className={s.formSection}>
        <Container>
          <div>
            <Row className={cn(sLay['layout-xs-column'], sLay['layout-gt-xs-row'], 'md')}>
              <Col className={cn('md', sLay['flex-gt-xs-60'])}>
                <InfoCard title="Billing Details" className={sMarg.mb30to15}>
                  <SimpleTable>
                    <div>
                      <div>Billing Account:</div>
                      <div>
                        <Link to={`/dashboard/billing/accounts/${service?.billingAccount}`}>
                          {service?.billingAccount}
                        </Link>
                      </div>
                    </div>
                  </SimpleTable>
                </InfoCard>

                {flow && (
                  <InfoCard title="Call Flows" className={sMarg.mb30to15}>
                    <div className={sText.descrip3} style={{ marginBottom: '20px' }}>
                      Call flows allow you to control what happens when people call your number. Set
                      up forwarding rules, IVR and more.
                    </div>
                    <SimpleTable style={{ marginBottom: '30px' }}>
                      <div>
                        <IconPhone size={20} style={{ marginRight: '10px' }} />
                        <div className={sLay.flex}>{flow?.name || 'Unnamed flow'}</div>
                        <div>
                          <Button
                            size="xs"
                            style={{ color: 'white' }}
                            color="tertiary"
                            to={`/dashboard/flows/${flow.id}`}
                          >
                            Edit
                          </Button>
                        </div>
                      </div>
                    </SimpleTable>
                  </InfoCard>
                )}

                {/* Extensions */}
                {/* ========== */}
                {service?.extensions && service?.extensions.length > 0 && (
                  <InfoCard title="User Extensions" className={sMarg.mb30to15}>
                    {deleteExtensionStatus.loading ? (
                      <LoadingOverlay transparent />
                    ) : (
                      <SimpleTable>
                        {service?.extensions.map(item => (
                          <div key={item.id}>
                            <div className={sLay.flex}>{item.number}</div>
                            <div>{item.customerReference}</div>
                            <div>
                              <Button
                                size="xs"
                                color="tertiary"
                                onClick={() =>
                                  props.showPhoneSystemExtensionEditFormModal({
                                    phoneSystemId,
                                    extensionId: item.id,
                                    number: item.number,
                                    name: item.customerReference || '',
                                    username: item.longNumber,
                                    password: item.password,
                                    displayNumber: item.portaDisplayNumber || '',
                                    didNumbers: service?.numbers || [],
                                  })
                                }
                              >
                                View / Edit
                              </Button>

                              {isAdmin ? (
                                <Button
                                  size="xs"
                                  color="warn"
                                  style={{ minWidth: '40px', marginLeft: '5px' }}
                                  ghost
                                  onClick={() =>
                                    props.handleDeleteItem(
                                      deleteExtensionMutation,
                                      {
                                        extensionId: item.id,
                                      },
                                      phoneSystemId
                                    )
                                  }
                                  disabled={deleteExtensionStatus.loading}
                                >
                                  X
                                </Button>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </SimpleTable>
                    )}
                  </InfoCard>
                )}

                {/* DID Numbers */}
                {/* =========== */}
                {service?.numbers && service?.numbers.length > 0 && (
                  <InfoCard title="DID Numbers" className={sMarg.mb30to15}>
                    {deleteNumberStatus.loading ? (
                      <LoadingOverlay transparent />
                    ) : (
                      <SimpleTable>
                        {service?.numbers.map(item => (
                          <div key={item.id}>
                            <div className={sLay.flex}>{item.number}</div>
                            <div>{item.customerReference}</div>
                            <div>
                              <Button
                                size="xs"
                                color="tertiary"
                                onClick={() =>
                                  props.showPhoneSystemNumberEditFormModal({
                                    phoneSystemNumberId: item.id,
                                    customerReference: item.customerReference || '',
                                    flowId: item.flow?.id || '',
                                  })
                                }
                              >
                                Edit
                              </Button>

                              {isAdmin ? (
                                <Button
                                  size="xs"
                                  color="warn"
                                  style={{ minWidth: '40px', marginLeft: '5px' }}
                                  ghost
                                  onClick={() =>
                                    props.handleDeleteItem(
                                      deleteNumberMutation,
                                      {
                                        phoneSystemNumberId: item.id,
                                      },
                                      phoneSystemId
                                    )
                                  }
                                  disabled={deleteNumberStatus.loading}
                                >
                                  X
                                </Button>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </SimpleTable>
                    )}
                  </InfoCard>
                )}

                {/* Hunt Groups */}
                {/* =========== */}
                {service?.huntGroups && service?.huntGroups.length > 0 && (
                  <InfoCard title="Hunt Groups" className={sMarg.mb30to15}>
                    {deleteHuntGroupStatus.loading ? (
                      <LoadingOverlay transparent />
                    ) : (
                      <>
                        {service?.huntGroups.map(item => (
                          <div key={item.id}>
                            <div
                              className={cn(
                                sLay['layout-row'],
                                sLay['layout-align-start-center'],
                                sMarg.mb15
                              )}
                            >
                              <div className={sLay.flex}>{item.number}</div>
                              <div>{item.customerReference}</div>
                              <div>
                                <Button
                                  size="xs"
                                  style={{ color: 'white', marginLeft: '5px' }}
                                  color="tertiary"
                                  onClick={async () => {
                                    await props.showPhoneSystemHuntGroupEditFormModal({
                                      phoneSystemId: service.id,
                                      huntGroupId: item.id,
                                      number: item.number,
                                      name: item.customerReference || '',
                                      extensionIds: item.extensionIds,
                                      huntSequence: item.portaHuntSequence || '',
                                    })
                                  }}
                                >
                                  Edit
                                </Button>

                                {isAdmin ? (
                                  <Button
                                    size="xs"
                                    color="warn"
                                    style={{ minWidth: '40px', marginLeft: '5px' }}
                                    ghost
                                    onClick={() =>
                                      props.handleDeleteItem(
                                        deleteHuntGroupMutation,
                                        {
                                          huntGroupId: item.id,
                                        },
                                        phoneSystemId
                                      )
                                    }
                                    disabled={deleteHuntGroupStatus.loading}
                                  >
                                    X
                                  </Button>
                                ) : null}
                              </div>
                            </div>
                            <div className={s.greyBox}>
                              {item.extensionIds.map((extensionId, idx) => {
                                const extension = service.extensions.find(
                                  item => item.id === extensionId
                                )
                                return (
                                  <div key={idx} className={cn(sLay['layout-row'], sMarg.mb5)}>
                                    <div className={sLay.flex}>{extension?.number}</div>
                                    <div>{extension?.customerReference}</div>
                                  </div>
                                )
                              })}
                              {item.extensionIds.length === 0 && <div>No extensions</div>}
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </InfoCard>
                )}
              </Col>

              <Col className={cn(sLay['flex-gt-xs-40'], 'md')}>
                <StickyChannel>
                  <div className={sLay['layout-column']}>
                    {isAdmin ? (
                      <>
                        <Button
                          className={sMarg.mb15}
                          color="warn"
                          size="sm"
                          rect
                          onClick={() => props.handleDeletePhoneSystem(deletePhoneSystemMutation)}
                          loading={deletePhoneSystemStatus.loading}
                          disabled={deletePhoneSystemStatus.loading}
                        >
                          Admin: Disconnect
                        </Button>

                        <br />

                        <Button
                          className={sMarg.mb15}
                          color="tertiary"
                          size="sm"
                          rect
                          onClick={() => {
                            props.showPhoneSystemExtensionEditFormModal({
                              phoneSystemId,
                              extensionId: '',
                              number: '',
                              name: '',
                              username: '',
                              password: '',
                              displayNumber: '',
                              didNumbers: service?.numbers || [],
                            })
                          }}
                        >
                          Admin: Add Extension
                        </Button>

                        <Button
                          className={sMarg.mb15}
                          color="secondary"
                          size="sm"
                          rect
                          onClick={() => {
                            props.showPhoneSystemNumberCreateFormModal({
                              phoneSystemId,
                              number: '',
                              customerReference: '',
                              flowId: '',
                            })
                          }}
                        >
                          Admin: Add DID Number
                        </Button>

                        <Button
                          className={sMarg.mb15}
                          color="primary"
                          size="sm"
                          rect
                          onClick={() => {
                            props.showPhoneSystemHuntGroupEditFormModal({
                              phoneSystemId,
                              huntGroupId: '',
                              extensionIds: [],
                              name: '',
                              number: '',
                              huntSequence: '',
                            })
                          }}
                        >
                          Admin: Add Hunt Group
                        </Button>
                      </>
                    ) : null}
                  </div>
                </StickyChannel>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
    </Page>
  )
}



// WEBPACK FOOTER //
// src/pages/Services/HostedVoice/PhoneSystemsEdit/PhoneSystemNewEdit.tsx